import React, { useCallback, useRef, useState } from 'react';
import { Download, Upload } from 'lucide-react';
import * as XLSX from 'xlsx';

import {
  XomicsApiError,
  confirmTranslationImport,
  loadAllTranslationsForExport,
  previewTranslationImport,
} from './api';
import { Empty, Failed, Loading } from './parts';
import { errorMessage } from './ui';

// errorMessage replaces any message with a generic one, which would swallow the
// checks below. Server codes still go through it; our own wording does not.
const SERVER_WORDING = {
  TRANSLATION_DRIFTED: 'Someone edited a translation since this preview.'
    + ' Upload the sheet again to see a fresh diff.',
  UNKNOWN_KEY: 'A key in the sheet is no longer in English. Export again and re-edit.',
  INVALID_CHANGE: 'One of the rows could not be read. Export again and re-edit.',
  DUPLICATE_CHANGE: 'The sheet lists the same key twice.',
};

function problem(error) {
  if (error instanceof XomicsApiError) {
    return SERVER_WORDING[error.code] ?? errorMessage(error);
  }
  return error?.message || 'Something went wrong';
}

// The header the export writes and the import reads back. Only the key and the
// translation are used on the way in; the reference is there to translate from.
const KEY_COLUMN = 'translation_key';
const REFERENCE_COLUMN = 'english_reference';
const VALUE_COLUMN = 'translation';

const PREVIEW_ROWS = 200;

function sheetName(languageCode) {
  return String(languageCode || 'translations').toUpperCase().slice(0, 31);
}

function cell(row, column) {
  const value = row?.[column];
  if (value === null || value === undefined) return '';
  return typeof value === 'string' ? value : String(value);
}

function Counts({ counts }) {
  const entries = [
    ['To change', counts.changed, 'is-primary'],
    ['Unchanged', counts.unchanged, ''],
    ['Duplicate keys', counts.duplicate, counts.duplicate > 0 ? 'is-warn' : ''],
    ['Unreadable rows', counts.invalid, counts.invalid > 0 ? 'is-warn' : ''],
    ['Unknown keys', counts.unknownKey, counts.unknownKey > 0 ? 'is-warn' : ''],
  ];
  return (
    <ul className="xo-import-counts">
      {entries.map(([label, value, tone]) => (
        <li key={label} className={tone}>
          <strong>{Number(value ?? 0).toLocaleString()}</strong>
          <span>{label}</span>
        </li>
      ))}
    </ul>
  );
}

// Export, edit in a spreadsheet, upload, look at the diff, then confirm. The
// preview writes nothing, so nothing is committed until Confirm is pressed.
export default function TranslationImport({ contentTypeId, languageCode, label, onApplied }) {
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const [status, setStatus] = useState('');
  const [preview, setPreview] = useState(null);
  const [fileName, setFileName] = useState('');
  const fileInput = useRef(null);

  const ready = contentTypeId !== '' && languageCode !== '';

  const reset = useCallback(() => {
    setPreview(null);
    setFileName('');
    setError('');
    setStatus('');
    if (fileInput.current) fileInput.current.value = '';
  }, []);

  const download = useCallback(async () => {
    setBusy('export');
    setError('');
    setStatus('');
    try {
      const rows = await loadAllTranslationsForExport({
        contentTypeId,
        languageCode,
        onProgress: (count) => setStatus(`Read ${count.toLocaleString()} rows`),
      });
      const sheet = XLSX.utils.json_to_sheet(rows.map((row) => ({
        [KEY_COLUMN]: row.translationKey,
        [REFERENCE_COLUMN]: row.englishReference ?? '',
        [VALUE_COLUMN]: row.value ?? '',
      })), { header: [KEY_COLUMN, REFERENCE_COLUMN, VALUE_COLUMN] });
      const book = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(book, sheet, sheetName(languageCode));
      XLSX.writeFile(book, `${label || 'translations'}-${languageCode}.xlsx`);
      setStatus(`Exported ${rows.length.toLocaleString()} rows`);
    } catch (requestError) {
      setError(problem(requestError));
    } finally {
      setBusy('');
    }
  }, [contentTypeId, languageCode, label]);

  const choose = useCallback(async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setBusy('preview');
    setError('');
    setStatus('');
    setPreview(null);
    setFileName(file.name);
    try {
      const buffer = await file.arrayBuffer();
      const book = XLSX.read(buffer, { type: 'array' });
      const first = book.SheetNames[0];
      if (!first) throw new Error('That file has no sheets');

      const parsed = XLSX.utils.sheet_to_json(book.Sheets[first], { defval: '' });
      if (parsed.length === 0) throw new Error('That sheet has no rows');
      if (!(KEY_COLUMN in parsed[0])) {
        throw new Error(`The sheet needs a "${KEY_COLUMN}" column`);
      }

      const rows = parsed.map((row) => ({
        translationKey: cell(row, KEY_COLUMN),
        value: cell(row, VALUE_COLUMN),
      }));
      const result = await previewTranslationImport({ contentTypeId, languageCode, rows });
      setPreview(result);
    } catch (requestError) {
      setError(problem(requestError));
    } finally {
      setBusy('');
    }
  }, [contentTypeId, languageCode]);

  const confirm = useCallback(async () => {
    if (!preview || preview.changes.length === 0) return;
    setBusy('confirm');
    setError('');
    try {
      const result = await confirmTranslationImport({
        contentTypeId,
        languageCode,
        source: fileName,
        changes: preview.changes.map((change) => ({
          translationKey: change.translationKey,
          previousValue: change.previousValue,
          newValue: change.newValue,
        })),
      });
      reset();
      setStatus(`Applied ${result.applied.toLocaleString()} changes`
        + ` (${result.created.toLocaleString()} filled in, ${result.updated.toLocaleString()} changed).`
        + ' Roll it back from the event log if it was wrong.');
      if (typeof onApplied === 'function') onApplied(result);
    } catch (requestError) {
      setError(problem(requestError));
    } finally {
      setBusy('');
    }
  }, [preview, contentTypeId, languageCode, fileName, reset, onApplied]);

  if (!ready) {
    return (
      <Empty
        title="Choose a content type and language"
        detail="The export and the upload both apply to one content type and one language."
      />
    );
  }

  const shown = preview ? preview.changes.slice(0, PREVIEW_ROWS) : [];

  return (
    <div className="xo-import">
      <div className="xo-import-actions">
        <button type="button" className="xo-btn-2" disabled={busy !== ''} onClick={download}>
          <Download aria-hidden="true" />
          {busy === 'export' ? 'Exporting…' : 'Export spreadsheet'}
        </button>

        <label className="xo-btn-2 xo-import-file">
          <Upload aria-hidden="true" />
          {busy === 'preview' ? 'Reading…' : 'Import spreadsheet'}
          <input
            ref={fileInput}
            type="file"
            accept=".xlsx,.xls,.csv"
            className="xo-hidden"
            disabled={busy !== ''}
            onChange={choose}
          />
        </label>

        {preview && (
          <button type="button" className="xo-link" disabled={busy !== ''} onClick={reset}>
            Discard
          </button>
        )}
      </div>

      <p className="xomics-field-hint">
        Export as three columns: <code>{KEY_COLUMN}</code>, <code>{REFERENCE_COLUMN}</code>,
        {' '}<code>{VALUE_COLUMN}</code>. Note: Only edit the third column. Keys that do not already exist as english is skipped.
      </p>

      {status && <p className="xo-alert t-ok" role="status">{status}</p>}
      {error && <Failed message={error} />}
      {busy === 'confirm' && <Loading label="Applying the changes" />}

      {preview && (
        <div className="xo-import-preview">
          <div className="xo-section-heading">
            <div>
              <h3>{fileName}</h3>
              <p>Nothing has been saved yet. This is what Confirm would change.</p>
            </div>
          </div>

          <Counts counts={preview.counts} />

          {preview.changes.length === 0 ? (
            <Empty title="Nothing to change" detail="Every row matches what is already stored." />
          ) : (
            <>
              <table className="xo-table xo-import-table">
                <thead>
                  <tr><th>Key</th><th>Now</th><th>After</th></tr>
                </thead>
                <tbody>
                  {shown.map((change) => (
                    <tr key={change.translationKey}>
                      <td><code>{change.translationKey}</code></td>
                      <td>{change.operation === 'create'
                        ? <em className="xomics-field-hint">not translated</em>
                        : change.previousValue}</td>
                      <td>{change.newValue}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {preview.changes.length > shown.length && (
                <p className="xomics-field-hint">
                  Showing the first {shown.length.toLocaleString()} of
                  {' '}{preview.changes.length.toLocaleString()} changes.
                  Confirm applies all of them as one event.
                </p>
              )}

              <div className="xo-foot-split">
                <span>
                  {preview.changes.length.toLocaleString()} changes ready
                </span>
                <button type="button" className="xo-btn" disabled={busy !== ''} onClick={confirm}>
                  {busy === 'confirm'
                    ? 'Applying…'
                    : `Confirm ${preview.changes.length.toLocaleString()} changes`}
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
